import {
  TypographyBreakpointsXxl,
  TypographyBreakpointsXl,
  TypographyBreakpointsLg,
  TypographyBreakpointsMd,
  TypographyBreakpointsSm,
  TypographyBreakpointsXs
} from "@amzn/aws-brand-tokens";
import { isServer } from "@amzn/aws-marketing-experience-builder-template-helpers";
import { useEffect, useState } from "react";
const BREAKPOINT_NAMES = ["smallphone", "phone", "tablet", "desktop", "widescreen", "ultrawide"];
const BREAKPOINT_MAP = {
  smallphone: TypographyBreakpointsXs,
  phone: TypographyBreakpointsSm,
  tablet: TypographyBreakpointsMd,
  desktop: TypographyBreakpointsLg,
  widescreen: TypographyBreakpointsXl,
  ultrawide: TypographyBreakpointsXxl
};
const transformToInteg = (value) => parseInt(value, 10);
const DEFAULT_BREAKPOINTS = Object.fromEntries(
  BREAKPOINT_NAMES.map((name) => [name, transformToInteg(BREAKPOINT_MAP[name])])
);
let currentBreakpoint = BREAKPOINT_NAMES[0];
const watchers = /* @__PURE__ */ new Set();
const onBreakpointMatch = (breakpoint, matcher) => {
  if (matcher.matches) {
    currentBreakpoint = breakpoint;
    watchers.forEach((cb) => cb(currentBreakpoint));
  }
};
function addBreakpointWatcher(watcher) {
  watchers.add(watcher);
  return removeBreakpointWatcher.bind(null, watcher);
}
function removeBreakpointWatcher(watcher) {
  watchers.delete(watcher);
}
const getMediaQueryForBreakpoint = (breakpoint) => {
  const width = DEFAULT_BREAKPOINTS[breakpoint];
  const nextBreakpoint = BREAKPOINT_NAMES[BREAKPOINT_NAMES.indexOf(breakpoint) + 1];
  const nextWidth = DEFAULT_BREAKPOINTS[nextBreakpoint];
  return nextWidth ? `(min-width: ${width}px) and (max-width: ${nextWidth}px)` : `(min-width: ${width}px)`;
};
const BREAKPOINT_MATCHERS = BREAKPOINT_NAMES.reduce(
  (result, breakpoint) => {
    if (isServer()) {
      return result;
    }
    const matcher = matchMedia?.(getMediaQueryForBreakpoint(breakpoint));
    if (matcher.matches) {
      currentBreakpoint = breakpoint;
    }
    const listener = () => onBreakpointMatch(breakpoint, matcher);
    matcher.addEventListener ? matcher.addEventListener("change", listener) : matcher.addListener(listener);
    result[breakpoint] = matcher;
    return result;
  },
  {}
);
const useBreakpoint = (opts) => {
  const [breakpoint, setBreakpoint] = useState(opts?.serverBreakpoint || null);
  useEffect(() => {
    addBreakpointWatcher(setBreakpoint);
    () => removeBreakpointWatcher(setBreakpoint);
  }, []);
  useEffect(() => {
    setBreakpoint(currentBreakpoint);
  }, []);
  return breakpoint;
};
const useIsBreakpoint = (...breakpoints) => {
  const breakpoint = useBreakpoint();
  const [isBreakpoint, setIsBreakpoint] = useState(null);
  useEffect(() => {
    if (breakpoint) {
      setIsBreakpoint(breakpoints.includes(breakpoint));
    }
  }, [breakpoints, breakpoint]);
  return isBreakpoint;
};
const useIsMobile = () => {
  const breakpoint = useBreakpoint();
  const [isMobile, setIsMobile] = useState(null);
  useEffect(() => {
    if (breakpoint) {
      setIsMobile(breakpoint === "smallphone" || breakpoint === "phone");
    }
  }, [breakpoint]);
  return isMobile;
};
export {
  BREAKPOINT_MATCHERS,
  BREAKPOINT_NAMES,
  DEFAULT_BREAKPOINTS,
  addBreakpointWatcher,
  getMediaQueryForBreakpoint,
  removeBreakpointWatcher,
  useBreakpoint,
  useIsBreakpoint,
  useIsMobile
};
